import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import Portal from '@/components/portal';
import { BigButton } from '@/components/big-button';

/**
 * Configuration for the PopConfirm portal hook
 */
export interface UsePopConfirmPortalConfig {
  /** The ID of the DOM element to overlay with the popconfirm */
  targetElementId: string;
  /** Whether the portal is enabled/active */
  enabled?: boolean;
  /** The confirmation message to display */
  text?: string;
  /** Callback when user confirms - click will propagate after this */
  onConfirm: () => Promise<void> | void;
  /** Callback when user discards - click will be blocked */
  onDiscard?: () => void;
}

/**
 * Return type for the PopConfirm portal hook
 */
export interface UsePopConfirmPortalReturn {
  /** The portal element to render (should be placed in your component's JSX) */
  portalElement: JSX.Element | null;
}

/**
 * Hook to create a portal-based popconfirm overlay over a target element.
 * Intercepts clicks on the target element and shows a confirmation dialog.
 * On confirm, allows the click to propagate to the underlying element.
 * On discard, prevents the click from happening.
 *
 * @example
 * ```tsx
 * function MyWidget() {
 *   const { portalElement } = usePopConfirmPortal({
 *     targetElementId: 'my-button-id',
 *     enabled: true,
 *     text: 'Are you sure you want to click this button?',
 *     onConfirm: async () => {
 *       // Do something before allowing the click
 *       await saveChanges();
 *     },
 *     onDiscard: () => {
 *       // User cancelled
 *     },
 *   });
 *
 *   return (
 *     <>
 *       {portalElement}
 *       <div>My widget content</div>
 *     </>
 *   );
 * }
 * ```
 */
export function usePopConfirmPortal({
  targetElementId,
  enabled = true,
  text,
  onConfirm,
  onDiscard,
}: UsePopConfirmPortalConfig): UsePopConfirmPortalReturn {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<DOMRect | null>(null);
  const [showPopConfirm, setShowPopConfirm] = useState(false);
  const [temporarilyDisabled, setTemporarilyDisabled] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Track target element position
  const updatePosition = useCallback(() => {
    if (!enabled) return;

    const targetElement = document.getElementById(targetElementId);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setPosition(rect);
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [targetElementId, enabled]);

  // Setup position tracking
  useEffect(() => {
    if (!enabled) {
      setIsVisible(false);
      return undefined;
    }

    updatePosition();

    // Update on resize
    const resizeObserver = new ResizeObserver(updatePosition);
    const targetElement = document.getElementById(targetElementId);

    if (targetElement) {
      resizeObserver.observe(targetElement);
    }

    // Update on scroll
    window.addEventListener(`scroll`, updatePosition, true);
    window.addEventListener(`resize`, updatePosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener(`scroll`, updatePosition, true);
      window.removeEventListener(`resize`, updatePosition);
    };
  }, [targetElementId, enabled, updatePosition]);

  // Handle click interception
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Show the popconfirm
      setShowPopConfirm(true);
    },
    [],
  );

  // Handle confirmation
  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm();
      // Temporarily disable overlay to allow the next click through
      setTemporarilyDisabled(true);
      setTimeout(() => {
        setTemporarilyDisabled(false);
      }, 3000); // Re-enable after 3 seconds
    } finally {
      setShowPopConfirm(false);
    }
  }, [onConfirm]);

  // Handle discard
  const handleDiscard = useCallback(() => {
    onDiscard?.();
    setShowPopConfirm(false);
  }, [onDiscard]);

  // Create portal element
  const portalElement =
    enabled && isVisible && position && !temporarilyDisabled ? (
      <Portal selector="body">
        <Popover
          isOpen={showPopConfirm}
          positions={[`bottom`, `top`, `left`, `right`]}
          onClickOutside={handleDiscard}
          containerStyle={{ zIndex: `999999` }}
          content={({ position: popPosition, childRect, popoverRect }) => (
            <ArrowContainer
              position={popPosition}
              childRect={childRect}
              popoverRect={popoverRect}
              arrowColor="white"
              arrowSize={10}
              arrowStyle={{
                top: `1px`,
                filter: `drop-shadow(0px -1px 0px rgba(213, 217, 220, 0.36))`,
                opacity: 1,
              }}
            >
              <ConfirmDialogContainer>
                <div className="text">{text || `Are you sure?`}</div>
                <div className="buttons">
                  <BigButton
                    br="0.4rem"
                    border="1px solid #C8CACB"
                    background="white"
                    color="#97A0A8"
                    noShadow
                    noTransform
                    size="small"
                    fillWidth
                    onClick={handleDiscard}
                  >
                    Discard
                  </BigButton>
                  <BigButton
                    br="0.4rem"
                    onClick={handleConfirm}
                    border="1px solid black"
                    noTransform
                    size="small"
                    fillWidth
                    noShadow
                  >
                    Yes
                  </BigButton>
                </div>
              </ConfirmDialogContainer>
            </ArrowContainer>
          )}
        >
          <OverlayWrapper
            ref={overlayRef}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
              height: `${position.height}px`,
            }}
            onClick={handleOverlayClick}
          />
        </Popover>
      </Portal>
    ) : null;

  return { portalElement };
}

const OverlayWrapper = styled.div`
  position: fixed;
  z-index: 999998;
  cursor: pointer;
  background: transparent;
  pointer-events: auto;
`;

const ConfirmDialogContainer = styled.div`
  background: white;
  border: 1px solid rgba(213, 217, 220, 0.36);
  border-radius: 0.4rem;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.15),
    0 6px 7px 0 rgba(151, 210, 206, 0.22), 0 2px 7px 0 rgba(0, 0, 0, 0);
  padding: 2rem;
  max-width: 25rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  .text {
    font-family: Inter, serif;
    font-size: 1.2rem;
    line-height: 1.5;
  }

  .buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
`;
